import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Lock, ArrowRight, AlertCircle, CheckCircle, LogOut, Shield } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Valid access codes
const VALID_ACCESS_CODES = ["LPAMINDWAVE"];

const RichiestaAccessoPage = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = location.state?.user;
  
  const [accessCode, setAccessCode] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setChecking(true);
    setError("");

    const upperCode = accessCode.trim().toUpperCase();
    
    if (!VALID_ACCESS_CODES.includes(upperCode)) {
      setError("Codice non valido. Verifica il codice e riprova.");
      setChecking(false);
      return;
    }

    // Code is valid, activate subscription
    try {
      // 2026-06-14: if the user is a legacy account with requires_new_access_code=True,
      // ALL non-whitelisted endpoints (including /stripe/create-checkout) return 403
      // ACCESS_CODE_REQUIRED. We must first call verify-access-code to clear the flag,
      // then proceed with Stripe. This is idempotent: if the flag is already false
      // the endpoint just returns ok.
      try {
        await api.post('/auth/verify-access-code', { code: upperCode });
      } catch (verifyErr) {
        // Backend rejected the code — surface a clear error and abort
        const detail = verifyErr.response?.data?.detail || "Codice di accesso non valido";
        if (detail === "ACCESS_CODE_REQUIRED" || detail.includes("non valido")) {
          setError("Codice non valido. Verifica il codice e riprova.");
        } else {
          setError(`Errore: ${detail}. Riprova.`);
        }
        setChecking(false);
        return;
      }

      const response = await api.post('/stripe/create-checkout', {
        origin_url: window.location.origin,
        promo_code: upperCode
      });
      
      setSuccess(true);
      toast.success("Accesso attivato con successo!");
      
      // Redirect to app after short delay
      setTimeout(() => {
        navigate("/oggi", { replace: true, state: { user } });
      }, 1500);
      
    } catch (promoError) {
      if (process.env.NODE_ENV === 'development') {
        console.error("[RichiestaAccesso] Error:", promoError.response?.data || promoError.message);
      }
      const errorMsg = promoError.response?.data?.detail || promoError.message;
      
      if (errorMsg.includes("già un abbonamento")) {
        // Already has subscription, just redirect
        toast.success("Hai già un abbonamento attivo!");
        navigate("/oggi", { replace: true, state: { user } });
      } else if (errorMsg.includes("Not authenticated")) {
        // Session expired, need to re-login
        setError("Sessione scaduta. Effettua nuovamente il login.");
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 2000);
      } else {
        setError(`Errore: ${errorMsg}. Riprova.`);
      }
    }
    
    setChecking(false);
  };

  const handleLogout = () => {
    document.cookie = "session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    if (onLogout) onLogout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50/30 to-white flex flex-col">
      <header className="p-6">
        <div className="container mx-auto max-w-7xl flex items-center justify-between">
          <h1 className="font-heading text-xl font-semibold tracking-tight bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Revela
          </h1>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" /> Esci
          </Button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="p-8">
            
            {success ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Accesso Attivato!</h2>
                <p className="text-muted-foreground">
                  Reindirizzamento in corso...
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-amber-600" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Accesso Richiesto</h2>
                  <p className="text-muted-foreground">
                    Ciao <strong>{user?.name?.split(' ')[0] || 'utente'}</strong>!<br />
                    Per accedere a Revela è necessario un codice di accesso.
                  </p>
                </div>

                <form onSubmit={handleCodeSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Codice di accesso
                    </label>
                    <Input
                      type="text"
                      placeholder="Inserisci il codice"
                      value={accessCode}
                      onChange={(e) => {
                        setAccessCode(e.target.value.toUpperCase());
                        setError("");
                      }}
                      className="text-center text-lg tracking-widest uppercase h-12"
                      autoFocus
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <Button 
                    type="submit"
                    className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                    disabled={!accessCode.trim() || checking}
                  >
                    {checking ? "Verifica in corso..." : "Attiva accesso"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>

                <div className="mt-8 pt-6 border-t text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    Non hai un codice?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Il codice di accesso viene fornito ai partecipanti dei corsi di Laura Piccinini.
                  </p>
                  <div className="flex justify-center gap-4 mt-4 text-xs">
                    <a 
                      href="https://www.laurapiccinini.it" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:underline"
                    >
                      www.laurapiccinini.it
                    </a>
                    <a 
                      href="https://www.psicoapplicata.org" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:underline"
                    >
                      www.psicoapplicata.org
                    </a>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      <footer className="p-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-green-600" />
          <Link to="/privacy" className="text-xs text-green-700 hover:underline">
            I tuoi dati sono privati e solo tuoi — Leggi la Privacy Policy
          </Link>
        </div>
        <p className="text-xs text-muted-foreground">
          Revela © {new Date().getFullYear()} — Laura Piccinini
        </p>
      </footer>
    </div>
  );
};

export default RichiestaAccessoPage;
